import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email?.trim()) {
      setError(t('auth.emailRequired'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('auth.emailInvalid'));
      return;
    }
    setError('');
    setLoading(true);
    const { error: err } = await resetPassword(email.trim());
    setLoading(false);
    if (err) {
      setError(err?.message || t('auth.resetFailed'));
      return;
    }
    setSent(true);
  };

  return (
    <>
      <Helmet>
        <title>{t('auth.forgotPageTitle')}</title>
        <meta name="description" content={t('auth.forgotPageDescription')} />
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center p-4 md:p-6 lg:p-8 relative">
        <div className="w-full max-w-md">
          <Link
            to="/authentication"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
          >
            <Icon name="ArrowLeft" size={16} />
            {t('auth.backToLogin')}
          </Link>

          <div className="bg-card rounded-xl md:rounded-2xl shadow-elevation-lg border border-border p-6 md:p-8">
            <h1 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
              {t('auth.forgotTitle')}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {t('auth.forgotSubtitle')}
            </p>

            {sent ? (
              <div className="space-y-4">
                <div className="p-4 mb-4 bg-success/10 border border-success/20 rounded-lg">
                  <p className="text-sm text-foreground">
                    {t('auth.resetSentCheckEmail', { email })}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {t('auth.resetSentSpamHint')}
                  </p>
                </div>
                <Link to="/authentication">
                  <Button variant="outline" fullWidth>
                    {t('auth.backToLogin')}
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label={t('auth.email')}
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={error}
                  disabled={loading}
                  autoFocus
                />
                <Button type="submit" fullWidth loading={loading}>
                  {t('auth.sendResetLink')}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
